import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IPayPlanMatchesResponse, IPayPlansResponse } from "@/PayPlans/types/api";
import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import {
	CallActivityPerformanceFragment,
	CustomerSatisfactionStoreFragment,
	LongevityFragment,
	CommissionTiersFragment,
	BonusDefinitionFragment,
	MetricFragment,
	InputValueFragment,
	VolumeBonusFragment,
	VariableOperationsGpsBonusSetupFragment,
} from "@PayPlans/api/fragments/commons";

import {
	CrossStoreCommissionsFragment,
	DefaultCommissionsFragment,
	GrowthBonusFragment,
	MarketShareFragment,
	PinnacleFragment,
	RankingBonusFragment,
	SFEBonusFragment,
	MarketMakeFragment,
	OperatingCostsFragment,
} from "@PayPlans/api/fragments/managers/sales";

// Retrieve sales pay plans list
export async function getSalesDepartmentalPayPlans(locationId: string, position: string) {
	const query = gql`
		query getSalesDepartmentalPayPlans($locationId: String!, $position: String!) {
			getSalesDepartmentalPayPlans(locationId: $locationId, position: $position) {
				_id
				name
				position
				locked
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesDepartmentalPayPlans", IPayPlansResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSalesDepartmentalPayPlans || [];
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Retrieve sales pay plans
export async function getSalesDepartmentalPayPlan(locationId: string, id: string, position: string) {
	const query = gql`
		query getSalesDepartmentalPayPlan($locationId: String!, $id: String!, $position: String!) {
			getSalesDepartmentalPayPlan(locationId: $locationId, id: $id, position: $position) {
				_id
				locationId
				name
				position
				default {
					...DefaultCommissions
				}
				crossStoreCommissions {
					...CrossStoreCommissions
				}
				volumeBonus {
					...VolumeBonus
				}
				rankingBonus {
					...RankingBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				marketShare {
					...MarketShare
				}
				growthBonus {
					...GrowthBonus
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				sfe {
					...SFEBonus
				}
				pinnacle {
					...Pinnacle
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${CrossStoreCommissionsFragment}
		${DefaultCommissionsFragment}
		${VolumeBonusFragment}
		${RankingBonusFragment}
		${CallActivityPerformanceFragment}
		${MarketShareFragment}
		${GrowthBonusFragment}
		${CustomerSatisfactionStoreFragment}
		${SFEBonusFragment}
		${PinnacleFragment}
		${LongevityFragment}
		${CommissionTiersFragment}
		${BonusDefinitionFragment}
		${MarketMakeFragment}
		${MetricFragment}
		${InputValueFragment}
		${OperatingCostsFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		id,
		position,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"getSalesDepartmentalPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSalesDepartmentalPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

export async function getSalesDepartmentalPayPlansSidebarTree(locationId: string, position: string, dontGetAdditionalPositions = false) {
	const query = gql`
		query getSalesDepartmentalPayPlansSidebarTree($locationId: String!, $position: String!, $dontGetAdditionalPositions: Boolean) {
			getSalesDepartmentalPayPlansSidebarTree(locationId: $locationId, position: $position, dontGetAdditionalPositions: $dontGetAdditionalPositions) {
				defaultPayPlans {
					defaultPayPlan {
						_id
						name
						position
						pdfAvailability
					}
					newVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
					preOwnedVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
				}
				available {
					_id
					name
					position
					pdfAvailability
				}
				inProgress {
					_id
					name
					position
					pdfAvailability
				}
				currentlyAssigned {
					_id
					name
					position
					pdfAvailability
				}
				previouslyAssigned {
					_id
					name
					position
					pdfAvailability
				}
			}
		}
	`;

	const variables = {
		locationId,
		position,
		dontGetAdditionalPositions
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"getSalesDepartmentalPayPlansSidebarTree", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSalesDepartmentalPayPlansSidebarTree || [];
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Retrieve sales pay plans full and partial matches
export async function getSalesDepartmentalPayPlanMatches(locationId: string, payPlanId: string, position: string) {
	const query = gql`
		query getSalesDepartmentalPayPlanMatches($locationId: String!, $id: String!, $position: String!) {
			getSalesDepartmentalPayPlanMatches(id: $id, locationId: $locationId, position: $position) {
				locationId
				payPlanId
				fullMatchList
				partialMatchList
			}
		}
	`;

	const variables = {
		id: payPlanId,
		locationId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesDepartmentalPayPlanMatches", IPayPlanMatchesResponse> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getSalesDepartmentalPayPlanMatches;
	} catch (err) {
		console.error(err);
		return {
			locationId,
			payPlanId,
			fullMatchList: [],
			partialMatchList: [],
		};
	}
}
